import { motion } from 'framer-motion';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useGetTeams } from '../hooks/useGetTeams';

import { InfoTeams } from './InfoTeams';
import { ItemFixture } from './ItemFixture';
import { ItemPositions } from './ItemPositions';

import { Loader, Modal } from '@/components';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useTourney from '@/hooks/useTourney';
import ROUTES from '@/router/routes';
import {
  DatosParticipanteMiniLigasTO,
  PartidoMiniLigaTO,
  ResponseVerML,
} from '@/types/minileagues';
import { FADE_IN_OUT } from '@/utils';
import { GENERICS_WORDS } from '@/utils/constants';

interface Props {
  dataTable?: DatosParticipanteMiniLigasTO[] | PartidoMiniLigaTO[];
  variant: typeof GENERICS_WORDS.fixture | typeof GENERICS_WORDS.positions;
}

export const RankingTable = React.memo(({ dataTable, variant }: Props) => {
  const { isMobile, isDesktop } = useIsDesktop();
  const { tourneyData } = useTourney();
  const navigate = useNavigate();
  const [showModalRival, setShowModalRival] = useState(false);

  const viewML = useMemo(() => {
    return tourneyData.tourney as ResponseVerML;
  }, [tourneyData.tourney]);

  const { isLoading, data, isFetching, refetch } = useGetTeams(
    tourneyData.id,
    tourneyData.matchDay?.orden || 0,
    false
  );

  // llamo a ver equipos si es desktop y se llama el modal ver rival
  useEffect(() => {
    if (Boolean(showModalRival) && isDesktop) {
      refetch();
    }
  }, [isDesktop, refetch, showModalRival]);

  if (variant === GENERICS_WORDS.fixture) {
    return (
      <>
        <Loader isVisible={isLoading || isFetching} />
        <motion.div
          key={variant}
          animate={'show'}
          initial={'hide'}
          variants={FADE_IN_OUT}
        >
          <div className="w-[calc(100%+2rem)] -left-4 relative lg:left-auto lg:w-auto">
            {dataTable && (
              <>
                <div className="lg:h-auto rounded-lg bg-white">
                  {dataTable.map((item: PartidoMiniLigaTO, index: number) => {
                    const verRival = () => {
                      if (
                        viewML.fechasMiniLiga &&
                        item.p2?.part &&
                        item.p1?.part
                      ) {
                        const myMatch =
                          item.p2?.part.idUsuario === viewML.idUsuario ||
                          item.p1.part.idUsuario === viewML.idUsuario;

                        const fechaMLSelecc = viewML.fechasMiniLiga.find(
                          (day) => day.id === tourneyData.matchDay?.id
                        );

                        return (
                          myMatch &&
                          fechaMLSelecc &&
                          viewML.ordenFechaMiniligaActual ===
                            fechaMLSelecc.ordF &&
                          viewML.veda &&
                          viewML.estadoFechaMiniligaActual === 'NO_PUBLICADA'
                        );
                      }
                    };

                    return (
                      <ItemFixture
                        key={index}
                        data={item}
                        veda={Boolean(verRival())}
                        onClick={() => {
                          isMobile
                            ? navigate(ROUTES.minileagues.viewRival)
                            : setShowModalRival(true);
                        }}
                      />
                    );
                  })}
                </div>
                <Modal
                  className="bg-gdt-gray-7 pt-6 w-[500px] px-5 text-center overflow-auto"
                  controlledOnClose={() => setShowModalRival(false)}
                  isControlledOpen={Boolean(showModalRival) && isDesktop}
                  render={() => <InfoTeams data={data} />}
                  size="sm"
                  title="Formación de equipos"
                  titleClassName="mb-4 text-sm !text-gdt-gray"
                  variant="controlled"
                />
              </>
            )}
          </div>
        </motion.div>
      </>
    );
  }

  return (
    <motion.div
      key={variant}
      animate={'show'}
      initial={'hide'}
      variants={FADE_IN_OUT}
    >
      <div className="w-[calc(100%+2rem)] -left-4 relative lg:left-auto lg:w-auto">
        <div className="flex text-gdt-gray-4 font-bold text-xs h-10 lg:h-14 lg:mt-3 items-center px-4 lg:px-8 bg-gdt-gray-7 lg:bg-white">
          <p className="w-10 lg:w-[50px]">Pos</p>
          <p className="flex-1">DT</p>
          <div className="flex justify-between flex-1 items-center">
            <p className="w-6 lg:w-20 text-center">
              {isDesktop ? 'Jugados' : 'J'}
            </p>
            <p className="w-6 lg:w-20 text-center">
              {isDesktop ? 'Ganados' : 'G'}
            </p>
            <p className="w-6 lg:w-20 text-center">
              {isDesktop ? 'Empatados' : 'E'}
            </p>
            <p className="w-6 lg:w-20 text-center">
              {isDesktop ? 'Perdidos' : 'P'}
            </p>
            <p className="lg:max-w-none lg:w-16 max-w-10 text-right">
              Pos. Gral.
            </p>
          </div>
        </div>
        {Array.isArray(dataTable) && dataTable.length > 0 && (
          <>
            <div className="lg:h-auto mt-2 rounded-lg bg-white lg:px-8">
              {dataTable.map(
                (item: DatosParticipanteMiniLigasTO, index: number) => {
                  return (
                    <ItemPositions
                      key={index + `${item.part?.idUsuario}`}
                      data={item}
                      index={index}
                    />
                  );
                }
              )}
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
});

RankingTable.displayName = 'RankingTable';
